/**
 * The four roles, for the browser.
 *
 * ⚠️ The twin of the backend's `src/lib/roles.ts`, and it must stay identical to
 * it. The frontend cannot import across the two packages, and the interface has
 * to know the ladder before any request is made — whether to draw the role
 * editor, whether to offer the team view, what to call a role in a chip.
 *
 * **This file never decides anything.** Every rule it expresses is enforced
 * again on the server, which is the copy that matters; this one only decides
 * what is on SCREEN. A reader who edits it in their console gets a button that
 * 403s, not a privilege.
 */

export const ROLES = ['normal', 'teamleader', 'grit', 'owner'] as const;

export type Role = (typeof ROLES)[number];

const RANK: Record<Role, number> = {
  normal: 0,
  teamleader: 1,
  grit: 2,
  owner: 3,
};

export const DEFAULT_ROLE: Role = 'normal';

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/** Whatever arrived over the wire, as a role. Anything unknown reads as `normal`. */
export function roleOf(value: unknown): Role {
  return isRole(value) ? value : DEFAULT_ROLE;
}

export function atLeast(role: unknown, min: Role): boolean {
  return RANK[roleOf(role)] >= RANK[min];
}

/** Strictly above `teamleader` — `grit` and `owner`. The admin console gate. */
export function canUseConsole(role: unknown): boolean {
  return atLeast(role, 'grit');
}

export function canSeeTeam(role: unknown): boolean {
  return atLeast(role, 'teamleader');
}

export function canAssignRoles(role: unknown): boolean {
  return roleOf(role) === 'owner';
}

export const ROLE_LABEL: Record<Role, string> = {
  normal: 'Normal',
  teamleader: 'Team leader',
  grit: 'Grit (admin)',
  owner: 'Owner (super admin)',
};

/** One line on what each rung may do — the console's role picker shows these. */
export const ROLE_BLURB: Record<Role, string> = {
  normal: 'Standard user. Translates, and sees their own history.',
  teamleader: 'The above, plus every activity of everyone on their team.',
  grit: 'Admin. The whole console — users, logs, prompts, settings.',
  owner: 'Super admin. Everything, and the only role that can hand out roles.',
};
